import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  VStack,
  Text,
  Progress,
  Badge,
  Spinner,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";

const MESSAGE_ROTATION_INTERVAL = 10000; // 10 seconds

const WorkflowProgressUI = ({
  steps = [],
  featureGroups = null,
  currentStep = null,
  variant = "compact",
  showProgress = true,
  title = "Workflow Progress",
  onWorkflowComplete,
}) => {
  const [messageIndices, setMessageIndices] = useState({});
  const intervalRef = useRef(null);
  const stepsRef = useRef(steps);
  const featureGroupsRef = useRef(featureGroups);

  // Keep refs updated with latest props
  useEffect(() => {
    stepsRef.current = steps;
    featureGroupsRef.current = featureGroups;
  }, [steps, featureGroups]);

  // Get all running steps from refs (always fresh data)
  const getRunningStepsFromRefs = useCallback(() => {
    const currentFeatureGroups = featureGroupsRef.current;
    const currentSteps = stepsRef.current;

    if (currentFeatureGroups && currentFeatureGroups.length > 0) {
      const runningSteps = [];
      currentFeatureGroups.forEach((group) => {
        if (group.steps) {
          group.steps.forEach((step) => {
            if (step.status === "running" && step.loadingMessages?.length > 0) {
              runningSteps.push(step);
            }
          });
        }
      });
      return runningSteps;
    }
    return currentSteps.filter(
      (step) => step.status === "running" && step.loadingMessages?.length > 0
    );
  }, []);

  // Derive a signature from steps/featureGroups to detect status changes
  const getStepsSignature = useCallback(() => {
    if (featureGroups && featureGroups.length > 0) {
      return featureGroups
        .flatMap((g) => g.steps || [])
        .map((s) => `${s.id}:${s.status}`)
        .join(",");
    }
    return steps.map((s) => `${s.id}:${s.status}`).join(",");
  }, [steps, featureGroups]);

  const stepsSignature = getStepsSignature();

  // Handle rotation interval
  useEffect(() => {
    const runningSteps = getRunningStepsFromRefs();

    if (runningSteps.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initialize message indices for new running steps
    setMessageIndices((prev) => {
      const updated = { ...prev };
      runningSteps.forEach((step) => {
        if (updated[step.id] === undefined) {
          updated[step.id] = 0;
        }
      });
      return updated;
    });

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up new rotation interval that reads from refs
    intervalRef.current = setInterval(() => {
      const currentRunningSteps = getRunningStepsFromRefs();
      if (currentRunningSteps.length === 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        return;
      }

      setMessageIndices((prev) => {
        const updated = { ...prev };
        currentRunningSteps.forEach((step) => {
          const currentIndex = updated[step.id] || 0;
          const nextIndex = (currentIndex + 1) % step.loadingMessages.length;
          updated[step.id] = nextIndex;
        });
        return updated;
      });
    }, MESSAGE_ROTATION_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [stepsSignature, getRunningStepsFromRefs]);

  // Clean up indices for steps that are no longer running
  useEffect(() => {
    setMessageIndices((prev) => {
      const updated = { ...prev };
      let changed = false;

      Object.keys(updated).forEach((stepId) => {
        let step = steps.find((s) => s.id === stepId);
        if (!step && featureGroups) {
          featureGroups.forEach((group) => {
            const found = group.steps?.find((s) => s.id === stepId);
            if (found) step = found;
          });
        }
        if (!step || step.status !== "running") {
          delete updated[stepId];
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [stepsSignature, steps, featureGroups]);

  // Get the display description for a step
  const getStepDescription = (step) => {
    if (step.status === "running" && step.loadingMessages?.length > 0) {
      const index = messageIndices[step.id] || 0;
      return step.loadingMessages[index];
    }
    return step.description;
  };


  const progressPercentage =
    steps.length > 0 && currentStep !== null
      ? ((currentStep + 1) / steps.length) * 100
      : 0;

  const allStepsCompleted =
    steps.length > 0 && steps.every((step) => step.status === "completed");

  React.useEffect(() => {
    if (allStepsCompleted && steps.length > 0) {
      console.log("🎉 Workflow fully completed including database save!");
    }
  }, [allStepsCompleted, steps.length]);

  if (!steps || steps.length === 0) {
    return null;
  }

  const getStepDisplayStatus = (stepIndex, step) => {
    if (step.status === "running" || step.status === "error") {
      return step.status;
    }
    if (currentStep !== null && stepIndex < currentStep) {
      return "completed";
    }
    if (step.status === "completed") {
      return "completed";
    }
    return "pending";
  };

  const renderStep = (step, index) => (
    <Flex
      key={step.id || `step-${index}`}
      align="center"
      justify="space-between"
      borderRadius="10px"
      borderWidth="1px"
      borderColor="brand.100"
      bg="white"
      p={2}
      pl={4}
      pr={4}
    >
      <Flex align="left">
        {step.status === "running" ? (
          <Spinner size="xs" color="brand.700" mr={2} mt={1} />
        ) : step.status === "completed" ? (
          <Icon as={CheckIcon} color="green.500" boxSize={3} mr={2} mt={1} />
        ) : (
          <Box
            w={3}
            h={3}
            borderRadius="full"
            bg={step.status === "error" ? "red.500" : "brand.300"}
            mr={2}
          />
        )}
        <VStack align="start" spacing={0.5}>
          <Text
            fontSize="sm"
            color={
              step.status === "completed"
                ? "brand.800"
                : step.status === "running"
                  ? "brand.600"
                  : step.status === "error"
                    ? "red.600"
                    : "gray.400"
            }
            fontWeight={
              step.status === "completed"
                ? "bold"
                : step.status === "running"
                  ? "medium"
                  : "normal"
            }
            fontStyle={step.status === "completed" ? "bold" : "normal"}
          >
            {step.title}
          </Text>
          {getStepDescription(step) && (
            <Text
              fontSize="xs"
              color={
                step.status === "completed"
                  ? "brand.800"
                  : step.status === "running"
                    ? "brand.600"
                    : step.status === "error"
                      ? "red.600"
                      : "gray.400"
              }
              fontWeight="normal"
              fontStyle="normal"
            >
              {getStepDescription(step)}
            </Text>
          )}
        </VStack>
      </Flex>

      {step.status === "error" && (
        <Badge colorScheme="red" size="sm">
          ✗
        </Badge>
      )}
    </Flex>
  );

  const isFeatureGroupCompleted = (group) => {
    return (
      group.steps && group.steps.every((step) => step.status === "completed")
    );
  };

  const isFeatureGroupRunning = (group) => {
    return group.steps && group.steps.some((step) => step.status === "running");
  };

  if (variant === "compact") {
    if (featureGroups && featureGroups.length > 0) {
      return (
        <Box borderRadius="md" mb={4} width="95%">
          <VStack spacing={2} align="stretch">
            {featureGroups.map((group, groupIndex) => {
              const isCompleted = isFeatureGroupCompleted(group);
              const isRunning = isFeatureGroupRunning(group);

              return (
                <Box key={group.featureId || `group-${groupIndex}`}>
                  <Flex
                    align="center"
                    mb={1}
                    mt={groupIndex > 0 ? 2 : 0}
                    px={2}
                  >
                    {isCompleted ? (
                      <Icon
                        as={CheckIcon}
                        color="green.500"
                        boxSize={3}
                        mr={2}
                      />
                    ) : isRunning ? (
                      <Spinner size="xs" color="brand.600" mr={2} />
                    ) : (
                      <Box
                        w={3}
                        h={3}
                        borderRadius="full"
                        bg="gray.300"
                        mr={2}
                      />
                    )}
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color={
                        isCompleted
                          ? "green.600"
                          : isRunning
                            ? "brand.700"
                            : "gray.500"
                      }
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      {group.featureName}
                    </Text>
                  </Flex>

                  <VStack spacing={0} align="stretch" pl={2}>
                    {group.steps &&
                      group.steps.map((step, stepIndex) =>
                        renderStep(step, `${groupIndex}-${stepIndex}`)
                      )}
                  </VStack>
                </Box>
              );
            })}
          </VStack>
          {showProgress && currentStep !== null && (
            <Progress
              value={progressPercentage}
              size="sm"
              borderRadius="full"
              colorScheme="blackAlpha"
              mt={3}
            />
          )}
        </Box>
      );
    }

    return (
      <Box borderRadius="md" mb={4} width="95%">
        <VStack spacing={0} align="stretch">
          {steps.map((step, index) => renderStep(step, index))}
        </VStack>
        {showProgress && currentStep !== null && (
          <Progress
            value={progressPercentage}
            size="sm"
            borderRadius="full"
            colorScheme="blackAlpha"
            mt={3}
          />
        )}
      </Box>
    );
  }

  return (
    <Box w="100%">
      <VStack spacing={3} align="stretch">
        <Text fontSize="sm" fontWeight="bold" color="gray.700">
          {title}
        </Text>

        {showProgress && (
          <Progress
            value={progressPercentage}
            size="sm"
            colorScheme="blue"
            borderRadius="md"
          />
        )}

        <VStack spacing={2} align="stretch">
          {steps.map((step, index) => {
            const displayStatus = getStepDisplayStatus(index, step);

            return (
              <Flex
                key={step.id || index}
                align="center"
                justify="space-between"
              >
                <Flex align="center" flex="1">
                  {displayStatus === "running" ? (
                    <Spinner size="xs" color="blue.500" mr={3} />
                  ) : displayStatus === "completed" ? (
                    <Icon as={CheckIcon} color="green.500" boxSize={3} mr={3} />
                  ) : (
                    <Box
                      w={3}
                      h={3}
                      borderRadius="full"
                      bg={displayStatus === "error" ? "red.500" : "gray.300"}
                      mr={3}
                    />
                  )}
                  <VStack align="start" spacing={0.5}>
                    <Text
                      fontSize="sm"
                      color={
                        displayStatus === "completed"
                          ? "green.600"
                          : displayStatus === "running"
                            ? "blue.600"
                            : displayStatus === "error"
                              ? "red.600"
                              : "gray.700"
                      }
                      fontWeight={
                        displayStatus === "completed"
                          ? "semibold"
                          : displayStatus === "running"
                            ? "medium"
                            : "normal"
                      }
                      textDecoration={
                        displayStatus === "completed" ? "none" : "bold"
                      }
                      fontStyle={
                        displayStatus === "completed" ? "none" : "normal"
                      }
                    >
                      {step.title}
                    </Text>
                    {getStepDescription(step) && (
                      <Text
                        fontSize="xs"
                        color={
                          displayStatus === "completed"
                            ? "green.600"
                            : displayStatus === "running"
                              ? "blue.600"
                              : displayStatus === "error"
                                ? "red.600"
                                : "gray.700"
                        }
                        fontWeight="normal"
                        fontStyle="normal"
                      >
                        {getStepDescription(step)}
                      </Text>
                    )}
                  </VStack>
                </Flex>

                {displayStatus === "error" && (
                  <Badge colorScheme="red" size="sm">
                    ✗
                  </Badge>
                )}
              </Flex>
            );
          })}
        </VStack>
      </VStack>
    </Box>
  );
};

export default WorkflowProgressUI;
